import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import {
  RotateCcw, Check, X, ChevronRight, ChevronLeft, Brain,
  Target, Flame, BookMarked, Loader2, ArrowLeft, Zap, Trophy,
  BarChart3, AlertTriangle, Star
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import OfflineSyncIndicator from '../components/OfflineSyncIndicator';
import useOfflineReviews from '../hooks/useOfflineReviews';
import { useCelebration } from '../contexts/CelebrationContext';
import { useSounds } from '../hooks/useSounds';
import {
  cacheOfflineDeck,
  getCachedDeck,
  queueOfflineReview,
} from '../utils/offlineReviews';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mastery level labels and colors
const MASTERY = [
  { label: 'New', color: '#94a3b8', bg: 'bg-slate-500/20' },
  { label: 'Learning', color: '#f97316', bg: 'bg-orange-500/20' },
  { label: 'Familiar', color: '#eab308', bg: 'bg-yellow-500/20' },
  { label: 'Good', color: '#22c55e', bg: 'bg-green-500/20' },
  { label: 'Strong', color: '#3b82f6', bg: 'bg-blue-500/20' },
  { label: 'Mastered', color: '#a855f7', bg: 'bg-purple-500/20' },
];

// Flashcard component
const Flashcard = ({ item, flipped, onFlip }) => {
  const content = item.content || {};
  const isQuiz = item.byte_type === 'quiz';
  
  // Handle both data formats: {front, back} and {question, answer}
  const frontText = content.front || content.question || '';
  const backText = content.back || content.answer || '';

  return (
    <div
      className="relative w-full cursor-pointer perspective-1000"
      style={{ minHeight: 280 }}
      onClick={onFlip}
      data-testid="flashcard"
    >
      <AnimatePresence mode="wait">
        {!flipped ? (
          <motion.div
            key="front"
            initial={{ rotateY: 90, opacity: 0 }}
            animate={{ rotateY: 0, opacity: 1 }}
            exit={{ rotateY: -90, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="w-full p-8  bg-gradient-to-br from-slate-800 to-slate-900 border border-[var(--isp-glass-border)] flex flex-col items-center justify-center text-center"
            style={{ minHeight: 280 }}
          >
            <p className="text-xs uppercase tracking-widest text-[var(--isp-muted)] mb-4">
              {isQuiz ? 'Question' : 'Term'}
            </p>
            <p className="text-xl sm:text-2xl font-semibold text-white leading-relaxed">
              {isQuiz ? content.question : frontText}
            </p>
            {isQuiz && content.options && (
              <div className="mt-6 space-y-2 w-full max-w-md">
                {content.options.map((opt, i) => (
                  <div key={`item-${i}`} className="px-4 py-2 rounded-xl bg-white/5 border border-[var(--isp-glass-border)] text-[var(--isp-body)] text-sm">
                    {opt}
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-[var(--isp-muted)] mt-6">Tap to reveal answer</p>
          </motion.div>
        ) : (
          <motion.div
            key="back"
            initial={{ rotateY: -90, opacity: 0 }}
            animate={{ rotateY: 0, opacity: 1 }}
            exit={{ rotateY: 90, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="w-full p-8  bg-gradient-to-br from-indigo-900/50 to-purple-900/50 border border-purple-500/20 flex flex-col items-center justify-center text-center"
            style={{ minHeight: 280 }}
          >
            <p className="text-xs uppercase tracking-widest text-purple-300/60 mb-4">
              {isQuiz ? 'Answer' : 'Definition'}
            </p>
            <p className="text-xl sm:text-2xl font-semibold text-white leading-relaxed">
              {isQuiz ? content.correct : backText}
            </p>
            {isQuiz && content.explanation && (
              <p className="mt-4 text-sm text-purple-200/70 max-w-md">{content.explanation}</p>
            )}
            <p className="text-xs text-purple-300/30 mt-6">Rate your recall below</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Weakness Map Bar
const WeaknessBar = ({ subject }) => (
  <div className="space-y-1.5" data-testid={`weakness-bar-${subject.subject_id}`}>
    <div className="flex justify-between items-center">
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: subject.subject_color }} />
        <span className="text-sm text-[var(--isp-heading)] font-medium">{subject.subject_name}</span>
      </div>
      <span className="text-xs text-[var(--isp-body)]">
        {subject.mastery_pct}% mastery ({subject.total_items} items)
      </span>
    </div>
    <div className="h-3 bg-[var(--isp-glass-inner)] rounded-full overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${subject.mastery_pct}%` }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="h-full rounded-full"
        style={{
          backgroundColor: subject.mastery_pct >= 80 ? '#22c55e' : subject.mastery_pct >= 50 ? '#eab308' : '#ef4444'
        }}
      />
    </div>
    {subject.weak_items > 0 && (
      <p className="text-xs text-red-400/70 flex items-center gap-1">
        <AlertTriangle className="w-3 h-3" />
        {subject.weak_items} item{subject.weak_items > 1 ? 's' : ''} need attention
      </p>
    )}
  </div>
);

// Session summary after review
const SessionSummary = ({ results, onRestart, onExit }) => {
  const correct = results.filter(r => r.quality >= 3).length;
  const total = results.length;
  const pct = total > 0 ? Math.round((correct / total) * 100) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center space-y-6"
      data-testid="session-summary"
    >
      <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
        <Trophy className="w-10 h-10 text-white" />
      </div>
      <h2 className="text-3xl font-bold text-white">Review Complete!</h2>
      <div className="flex justify-center gap-8">
        <div className="text-center">
          <p className="text-4xl font-bold text-green-400">{correct}</p>
          <p className="text-xs text-[var(--isp-body)] mt-1">Correct</p>
        </div>
        <div className="text-center">
          <p className="text-4xl font-bold text-red-400">{total - correct}</p>
          <p className="text-xs text-[var(--isp-body)] mt-1">Missed</p>
        </div>
        <div className="text-center">
          <p className="text-4xl font-bold text-purple-400">{pct}%</p>
          <p className="text-xs text-[var(--isp-body)] mt-1">Score</p>
        </div>
      </div>
      <div className="flex gap-3 justify-center pt-4">
        <Button
          onClick={onRestart}
          className="rounded-xl gap-2 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
          data-testid="review-again-btn"
        >
          <RotateCcw className="w-4 h-4" />
          Review Again
        </Button>
        <Link to="/app">
          <Button variant="outline" className="rounded-xl gap-2 border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]" data-testid="back-dashboard-btn">
            <ArrowLeft className="w-4 h-4" />
            Dashboard
          </Button>
        </Link>
      </div>
    </motion.div>
  );
};

const ReviewPage = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [results, setResults] = useState([]);
  const [sessionDone, setSessionDone] = useState(false);
  const [weaknessMap, setWeaknessMap] = useState(null);
  const [srsStats, setSrsStats] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { checkAndCelebrate } = useCelebration();
  const { play: playSound } = useSounds();

  // Lecture focus — when set, the review queue is narrowed to bytes
  // generated from that specific lecture. Persisted to localStorage so
  // the user's choice survives reloads but defaults to 'all' on a new
  // session if the lecture has since been deleted.
  const [lectures, setLectures] = useState([]);
  const [lectureFilter, setLectureFilter] = useState(() => {
    try { return localStorage.getItem('istudypro_review_lecture') || 'all'; } catch { return 'all'; }
  });

  // Offline sync wiring — pending count, online/offline status, manual
  // syncNow button. The indicator hangs in the page header; the auto
  // syncNow on 'online' is handled inside the hook.
  const offline = useOfflineReviews();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const nextUrl = lectureFilter && lectureFilter !== 'all'
        ? `${API}/srs/next-set?count=20&lecture_id=${encodeURIComponent(lectureFilter)}`
        : `${API}/srs/next-set?count=20`;
      const [nextRes, weakRes, statsRes, lecturesRes, cacheRes] = await Promise.all([
        axios.get(nextUrl),
        axios.get(`${API}/srs/weakness-map`),
        axios.get(`${API}/srs/stats`),
        axios.get(`${API}/lectures`),
        // Best-effort cache prime — runs in parallel but its failure
        // doesn't kill the page load (caught individually below).
        axios.get(`${API}/srs/offline-cache?count=40`).catch(() => null),
      ]);
      setItems(nextRes.data.items || []);
      setWeaknessMap(weakRes.data);
      setSrsStats(statsRes.data);
      setLectures(Array.isArray(lecturesRes.data) ? lecturesRes.data : (lecturesRes.data?.lectures || []));
      // Persist the offline deck so the next no-network review session
      // starts instantly. We cache the broader 40-card window (not the
      // narrower next-set slice) so the offline experience isn't tied
      // to the current Focus filter.
      if (cacheRes?.data?.cards) {
        cacheOfflineDeck(cacheRes.data.cards, cacheRes.data.cached_at);
      }
    } catch (err) {
      console.error('Error fetching SRS data:', err);
      // Offline fallback: hydrate items from the cached deck instead of
      // leaving the user with a blank, broken page.
      const cached = getCachedDeck();
      if (cached?.cards?.length) {
        setItems(cached.cards);
        toast.info(`Reviewing from offline cache (${cached.cards.length} cards)`);
      } else {
        toast.error('Failed to load review data');
      }
    } finally {
      setLoading(false);
    }
  }, [lectureFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startReview = () => {
    setReviewing(true);
    setCurrentIdx(0);
    setFlipped(false);
    setResults([]);
    setSessionDone(false);
  };

  const handleRate = async (quality) => {
    if (submitting) return;
    const item = items[currentIdx];
    if (!item) return;
    setSubmitting(true);

    // Always queue the grade locally first. The queue is the single
    // source of truth — whether we're online or offline, every grade
    // goes through the same path so retention metrics stay consistent.
    queueOfflineReview({ byte_id: item.id, quality });
    setResults(prev => [...prev, { id: item.id, quality }]);

    // iter193 — micro-cue on every successful rating. Quality 3+ is the
    // SM-2 threshold for "remembered correctly"; below that we stay
    // quiet (no negative-reinforcement sound, by design).
    if (quality >= 3) playSound('card-correct');

    // Advance UI immediately regardless of network. The user doesn't
    // wait on the round-trip.
    if (currentIdx + 1 >= items.length) {
      setSessionDone(true);
      // iter192 retention — finishing a review session is a prime moment
      // for streak / mastery badge unlocks. Fire after the UI advances
      // so the celebration lands on top of the SessionSummary screen.
      checkAndCelebrate();
    } else {
      setCurrentIdx(prev => prev + 1);
      setFlipped(false);
    }
    setSubmitting(false);

    // If we're online, kick off a fire-and-forget sync so the queue
    // doesn't grow unbounded mid-session. The hook handles concurrency.
    if (navigator.onLine) {
      offline.syncNow().catch(() => { /* indicator already updated */ });
    }
  };

  const restartReview = () => {
    fetchData();
    setReviewing(false);
    setSessionDone(false);
    setResults([]);
    setCurrentIdx(0);
    setFlipped(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="review-loading">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  const currentItem = items[currentIdx];

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="review-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1 className="font-serif-accent text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--isp-heading)] tracking-tight flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <BookMarked className="w-6 h-6 text-white" />
            </div>
            Quick Review
          </h1>
          <p className="text-[var(--isp-body)] mt-2 text-base sm:text-lg">
            Spaced repetition for long-term retention
          </p>
          <div className="mt-3">
            <OfflineSyncIndicator
              isOnline={offline.isOnline}
              pendingCount={offline.pendingCount}
              lastSyncAt={offline.lastSyncAt}
              syncStatus={offline.syncStatus}
              onSync={offline.syncNow}
            />
          </div>
        </div>
        {!reviewing && (
          <Button
            onClick={startReview}
            disabled={items.length === 0}
            className="gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl shadow-lg shadow-amber-500/25"
            data-testid="start-review-btn"
          >
            <Zap className="w-4 h-4" />
            Start Review ({items.length} items)
          </Button>
        )}
      </div>

      {/* Lecture focus selector — restricts the review queue to a
          single lecture's bytes. Hidden mid-session so the user can't
          accidentally swap context mid-card. */}
      {!reviewing && lectures.length > 0 && (
        <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-xl bg-[var(--isp-glass)] border border-[var(--isp-glass-border)]" data-testid="review-lecture-filter-wrap">
          <Target className="w-3.5 h-3.5 text-[var(--isp-muted)] shrink-0" />
          <span className="text-xs text-[var(--isp-muted)] shrink-0">Focus on lecture:</span>
          <select
            value={lectureFilter}
            onChange={(e) => {
              setLectureFilter(e.target.value);
              try { localStorage.setItem('istudypro_review_lecture', e.target.value); } catch { /* ignore */ }
            }}
            className="flex-1 bg-transparent text-xs text-[var(--isp-heading)] focus:outline-none cursor-pointer font-medium"
            data-testid="review-lecture-filter"
          >
            <option value="all">All lectures</option>
            {lectures.map((l) => (
              <option key={l.id} value={l.id}>{l.title || l.topic || 'Untitled'}</option>
            ))}
          </select>
        </div>
      )}

      {/* Stats row */}
      {srsStats && !reviewing && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: 'Total Items', value: srsStats.total_items, icon: BookMarked, color: 'from-blue-500 to-cyan-500' },
            { label: 'Due Now', value: srsStats.due_now, icon: AlertTriangle, color: 'from-red-500 to-rose-500' },
            { label: 'Mastered', value: srsStats.mastered, icon: Star, color: 'from-purple-500 to-indigo-500' },
            { label: 'Reviewed', value: srsStats.reviewed, icon: Check, color: 'from-green-500 to-emerald-500' },
            { label: 'Struggling', value: srsStats.struggling, icon: Target, color: 'from-orange-500 to-amber-500' },
          ].map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.label} className="p-4  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-xl bg-gradient-to-br ${stat.color}`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stat.value}</p>
                    <p className="text-xs text-[var(--isp-body)]">{stat.label}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Review mode */}
      {reviewing && !sessionDone && currentItem && (
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Progress */}
          <div className="flex items-center gap-4">
            <div className="flex-1 h-2 bg-[var(--isp-glass-inner)] rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${((currentIdx + 1) / items.length) * 100}%` }}
              />
            </div>
            <span className="text-sm text-[var(--isp-body)] font-mono">{currentIdx + 1}/{items.length}</span>
          </div>

          {/* Subject tag */}
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: currentItem.subject_color }} />
            <span className="text-sm text-[var(--isp-body)]">{currentItem.subject_name}</span>
            <span className={`ml-auto text-xs px-2 py-1 rounded-lg ${MASTERY[currentItem.mastery_level]?.bg || 'bg-slate-500/20'}`}
              style={{ color: MASTERY[currentItem.mastery_level]?.color }}>
              {MASTERY[currentItem.mastery_level]?.label}
            </span>
          </div>

          {/* Flashcard */}
          <Flashcard item={currentItem} flipped={flipped} onFlip={() => setFlipped(!flipped)} />

          {/* Rating buttons (show after flip) */}
          <AnimatePresence>
            {flipped && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="space-y-3"
              >
                <p className="text-center text-sm text-[var(--isp-muted)]">How well did you know this?</p>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {[
                    { q: 0, label: 'Blank', color: 'bg-red-500/20 text-red-300 border-red-500/30 hover:bg-red-500/30' },
                    { q: 1, label: 'Wrong', color: 'bg-red-500/15 text-red-300/80 border-red-500/20 hover:bg-red-500/25' },
                    { q: 2, label: 'Hard', color: 'bg-orange-500/15 text-orange-300 border-orange-500/20 hover:bg-orange-500/25' },
                    { q: 3, label: 'Okay', color: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/20 hover:bg-yellow-500/25' },
                    { q: 4, label: 'Good', color: 'bg-green-500/15 text-green-300 border-green-500/20 hover:bg-green-500/25' },
                    { q: 5, label: 'Easy', color: 'bg-purple-500/15 text-purple-300 border-purple-500/20 hover:bg-purple-500/25' },
                  ].map(({ q, label, color }) => (
                    <Button
                      key={q}
                      variant="outline"
                      size="sm"
                      disabled={submitting}
                      onClick={() => handleRate(q)}
                      className={`rounded-xl ${color}`}
                      data-testid={`rate-${q}`}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Session done */}
      {sessionDone && (
        <SessionSummary results={results} onRestart={restartReview} />
      )}

      {/* Weakness Map (shown when not reviewing) */}
      {!reviewing && weaknessMap && weaknessMap.subjects?.length > 0 && (
        <Card className="p-6  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]" data-testid="weakness-map">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-xl bg-gradient-to-br from-red-500 to-rose-500">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Weakness Map</h2>
              <p className="text-sm text-[var(--isp-body)]">
                Overall mastery: {weaknessMap.overall_mastery_pct}% across {weaknessMap.total_items} items
              </p>
            </div>
          </div>
          <div className="space-y-5">
            {weaknessMap.subjects.map((subj) => (
              <WeaknessBar key={subj.subject_id} subject={subj} />
            ))}
          </div>
        </Card>
      )}

      {/* Mastery distribution */}
      {!reviewing && srsStats && srsStats.total_items > 0 && (
        <Card className="p-6  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]" data-testid="mastery-distribution">
          <h3 className="text-lg font-semibold text-[var(--isp-heading)] mb-4 flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-400" />
            Mastery Distribution
          </h3>
          <div className="flex items-end gap-2 h-32">
            {srsStats.mastery_distribution.map((count, level) => {
              const pct = srsStats.total_items > 0 ? (count / srsStats.total_items) * 100 : 0;
              return (
                <div key={level} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs text-[var(--isp-body)]">{count}</span>
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${Math.max(pct, 4)}%` }}
                    transition={{ duration: 0.5, delay: level * 0.1 }}
                    className="w-full rounded-t-lg"
                    style={{ backgroundColor: MASTERY[level]?.color, minHeight: 4 }}
                  />
                  <span className="text-[10px] text-[var(--isp-muted)]">{MASTERY[level]?.label}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Empty state */}
      {!reviewing && items.length === 0 && (
        <Card className="p-12  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)] text-center" data-testid="empty-review">
          <BookMarked className="w-16 h-16 mx-auto mb-4 text-[var(--isp-muted)]" />
          <h3 className="text-xl font-medium text-[var(--isp-heading)] mb-2">No items to review</h3>
          <p className="text-[var(--isp-body)] mb-6">
            Generate learning bytes from your lectures to start reviewing!
          </p>
          <Link to="/lectures">
            <Button className="rounded-xl gap-2 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600">
              <Zap className="w-4 h-4" />
              Go to Lectures
            </Button>
          </Link>
        </Card>
      )}
    </div>
  );
};

export default ReviewPage;
